import React from 'react';
import { Car } from 'lucide-react';

interface VehicleDetailsProps {
  formData: any;
  setFormData: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
  /** Look up an existing HA number and copy vehicle + customer details. */
  onHaNumberCheck?: (haNumber: string) => void | Promise<void>;
  haNumberChecking?: boolean;
  onClearHaData?: () => void;
  showClearHaData?: boolean;
}

export function VehicleDetails({
  formData,
  setFormData,
  onNext,
  onBack,
  onHaNumberCheck,
  haNumberChecking,
  onClearHaData,
  showClearHaData,
}: VehicleDetailsProps) {
  return (
    <div className="app-card overflow-hidden">
      <div className="app-card-inner">
        <div className="app-section-head py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center border border-emerald-800 bg-emerald-800 text-white">
              <Car className="h-5 w-5" />
            </div>
            <div>
              <h2 className="app-section-title">Step 1 — Vehicle</h2>
              <p className="app-section-desc">Registration, identifiers, and status.</p>
            </div>
          </div>
        </div>

        <form className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label className="app-label">HA number</label>
            {showClearHaData ? (
              <div className="mb-1.5 flex justify-end sm:mb-2">
                <button
                  type="button"
                  className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-100 active:bg-rose-100"
                  onClick={() => onClearHaData?.()}
                >
                  Clear
                </button>
              </div>
            ) : null}
            <div className="flex flex-row gap-2">
              <input
                type="text"
                className="app-field min-w-0 flex-1"
                value={formData.haNumber}
                onChange={(e) => setFormData({ ...formData, haNumber: e.target.value })}
                placeholder="Enter HA number"
              />
              <button
                type="button"
                className="app-btn-secondary shrink-0 px-4"
                disabled={!formData.haNumber.trim() || haNumberChecking}
                onClick={() => onHaNumberCheck?.(formData.haNumber)}
              >
                {haNumberChecking ? 'Checking…' : 'Check'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-zinc-400">
              Check copies vehicle + customer details from main ledger (not amount / EMI). Seed examples: HA-2401, HA-2412, HA-2503…
            </p>
          </div>
          <div>
            <label className="app-label">Registration number</label>
            <input
              type="text"
              className="app-field uppercase"
              value={formData.regNo}
              autoCapitalize="characters"
              onChange={(e) => setFormData({ ...formData, regNo: e.target.value.toUpperCase() })}
              placeholder="e.g. TS09AB1234"
            />
            <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">Always saved in CAPITAL letters</p>
          </div>
          <div>
            <label className="app-label">Makers</label>
            <input type="text" className="app-field" value={formData.makers} onChange={(e) => setFormData({ ...formData, makers: e.target.value })} />
          </div>
          <div>
            <label className="app-label">Model</label>
            <input type="text" className="app-field" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
          </div>
          <div>
            <label className="app-label">Chassis number</label>
            <input
              type="text"
              className="app-field uppercase"
              value={formData.chasisNo}
              autoCapitalize="characters"
              onChange={(e) => setFormData({ ...formData, chasisNo: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <label className="app-label">Engine number</label>
            <input
              type="text"
              className="app-field uppercase"
              value={formData.engNo}
              autoCapitalize="characters"
              onChange={(e) => setFormData({ ...formData, engNo: e.target.value.toUpperCase() })}
            />
          </div>

          <div className="flex flex-wrap items-center gap-6 md:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700 dark:text-zinc-300">
              <input type="checkbox" className="h-4 w-4 border-slate-400 text-emerald-700 focus:ring-emerald-500" checked={formData.isCB} onChange={(e) => setFormData({ ...formData, isCB: e.target.checked })} />
              CB
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700 dark:text-zinc-300">
              <input type="checkbox" className="h-4 w-4 border-slate-400 text-emerald-700 focus:ring-emerald-500" checked={formData.isCLR} onChange={(e) => setFormData({ ...formData, isCLR: e.target.checked })} />
              CLR
            </label>
          </div>

          <div>
            <label className="app-label">H.P.N date</label>
            <input type="date" className="app-field" value={formData.hpnDate} onChange={(e) => setFormData({ ...formData, hpnDate: e.target.value })} />
          </div>
        </form>

        <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5">
          {onBack && (
            <button type="button" onClick={onBack} className="app-btn-secondary">Back</button>
          )}
          <button type="button" onClick={onNext} className="app-btn-primary">Continue</button>
        </div>
      </div>
    </div>
  );
}
